/**
 * Created by neo on 21.01.2026
 * Multi Media Upload Input Component - For uploading images and videos with preview
 */
import * as React from 'react';
import { useRef, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { Theme } from '../Theme/Theme';

export type UploadedMedia = {
  file: File;
  previewUrl: string;
  type: 'image' | 'video';
};

export type MultiMediaUploadInputProps = {
  media: UploadedMedia[];
  onChange: (media: UploadedMedia[]) => void;
  maxMedia?: number;
  maxSizeMB?: number;
  disabled?: boolean;
};

const Container = styled.div`
  width: 100%;
`;

const UploadArea = styled.div<{ disabled?: boolean }>`
  border: 2px dashed ${Theme.Colors.lighterGrey};
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s;
  background: ${(props) => (props.disabled ? Theme.Colors.lightestGrey : 'white')};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  &:hover {
    border-color: ${(props) => (props.disabled ? Theme.Colors.lighterGrey : Theme.Colors.primary)};
    background: ${(props) => (props.disabled ? Theme.Colors.lightestGrey : `${Theme.Colors.primary}05`)};
  }
`;

const UploadIcon = styled.div`
  font-size: 36px;
  margin-bottom: 8px;
`;

const UploadText = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${Theme.Colors.textColor};
  margin: 0 0 4px 0;
`;

const UploadHint = styled.p`
  font-size: 12px;
  color: ${Theme.Colors.warmGrey};
  margin: 0;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
`;

const PreviewItem = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: ${Theme.Colors.lightestGrey};
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const VideoOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  pointer-events: none;
`;

const PlayIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  z-index: 2;

  &:hover {
    background: ${Theme.Colors.error};
  }
`;

const MediaTypeLabel = styled.span`
  position: absolute;
  bottom: 6px;
  left: 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
`;

const ErrorText = styled.p`
  font-size: 12px;
  color: ${Theme.Colors.error};
  margin: 8px 0 0 0;
`;

const HiddenInput = styled.input`
  display: none;
`;

const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/mov'];
const ALL_ACCEPTED_TYPES = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES];

export const MultiMediaUploadInput: React.FC<MultiMediaUploadInputProps> = ({
  media,
  onChange,
  maxMedia = 3,
  maxSizeMB = 1024,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>('');

  const handleClick = useCallback(() => {
    if (!disabled && media.length < maxMedia) {
      fileInputRef.current?.click();
    }
  }, [disabled, media.length, maxMedia]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;

      setError('');

      const remainingSlots = maxMedia - media.length;
      const filesToAdd = files.slice(0, remainingSlots);

      const maxSizeBytes = maxSizeMB * 1024 * 1024;

      const newMedia: UploadedMedia[] = [];

      for (const file of filesToAdd) {
        const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type);
        const isVideo = ACCEPTED_VIDEO_TYPES.includes(file.type);

        if (!isImage && !isVideo) {
          setError('Some files were skipped. Only images (PNG, JPEG, GIF, WebP) and videos (MP4, MOV, WebM) are allowed.');
          continue;
        }

        if (file.size > maxSizeBytes) {
          const sizeDisplay = maxSizeMB >= 1024 ? `${maxSizeMB / 1024}GB` : `${maxSizeMB}MB`;
          setError(`Some files were skipped. Maximum size is ${sizeDisplay} per file.`);
          continue;
        }

        const previewUrl = URL.createObjectURL(file);
        newMedia.push({
          file,
          previewUrl,
          type: isVideo ? 'video' : 'image',
        });
      }

      if (newMedia.length > 0) {
        onChange([...media, ...newMedia]);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [maxSizeMB, maxMedia, media, onChange],
  );

  const handleRemove = useCallback(
    (index: number) => {
      const mediaToRemove = media[index];
      if (mediaToRemove?.previewUrl) {
        URL.revokeObjectURL(mediaToRemove.previewUrl);
      }
      const newMedia = media.filter((_, i) => i !== index);
      onChange(newMedia);
    },
    [media, onChange],
  );

  const canAddMore = media.length < maxMedia && !disabled;

  return (
    <Container>
      {media.length > 0 && (
        <PreviewGrid>
          {media.map((item, index) => (
            <PreviewItem key={index}>
              {item.type === 'video' ? (
                <>
                  <PreviewVideo src={item.previewUrl} muted />
                  <VideoOverlay>
                    <PlayIcon>▶</PlayIcon>
                  </VideoOverlay>
                </>
              ) : (
                <PreviewImage src={item.previewUrl} alt={`Upload ${index + 1}`} />
              )}
              <RemoveButton onClick={() => handleRemove(index)} type="button" title="Remove" disabled={disabled}>
                ×
              </RemoveButton>
              <MediaTypeLabel>{item.type === 'video' ? 'Video' : 'Photo'}</MediaTypeLabel>
            </PreviewItem>
          ))}
        </PreviewGrid>
      )}

      {canAddMore && (
        <UploadArea onClick={handleClick} disabled={disabled}>
          <UploadIcon>📸</UploadIcon>
          <UploadText>{media.length === 0 ? 'Add photos or videos' : 'Add more media'}</UploadText>
          <UploadHint>
            Images or videos, max {maxSizeMB >= 1024 ? `${maxSizeMB / 1024}GB` : `${maxSizeMB}MB`} per file ({media.length}/{maxMedia})
          </UploadHint>
        </UploadArea>
      )}

      <HiddenInput ref={fileInputRef} type="file" accept={ALL_ACCEPTED_TYPES.join(',')} onChange={handleFileChange} multiple />

      {error && <ErrorText>{error}</ErrorText>}
    </Container>
  );
};
